import { stringAvatar } from '@/utils/string-avatar';
import {
    PhotoLibraryRounded,
    ImageRounded,
    VideoCameraBackRounded,
    LinkRounded,
    OpenInNewRounded,
    ImageNotSupportedRounded,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Divider,
    Icon,
    IconButton,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type PostCard = {
    profile?: {
        avatar?: string;
        net?: string;
        name?: string;
    };
    caption?: string;
    image?: string;
    url?: string;
    publishDate?: string;
    format?: string;
    /** The first metric is the card's headline number, so it should be the one the posts are ranked by. */
    metrics: {
        id?: string;
        name: string;
        value?: string;
    }[];
};

const IMAGE_HEIGHT = 220;

const metricsGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 2,
    rowGap: 1,
};

export const PostCardLoading = () => {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Skeleton variant="rounded" width="100%" height={IMAGE_HEIGHT} />
                <Skeleton variant="rounded" width="50%" height={16} />
                <Stack gap={0.5}>
                    <Skeleton variant="rounded" width="100%" height={14} />
                    <Skeleton variant="rounded" width="100%" height={14} />
                    <Skeleton variant="rounded" width="70%" height={14} />
                </Stack>
                <Divider />
                <Skeleton variant="rounded" width="40%" height={28} />
                <Box sx={metricsGridSx}>
                    {Array.from(new Array(4)).map((_, key) => (
                        <Skeleton key={key} variant="rounded" height={32} />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

const MetricValue = ({ value, variant }: { value?: string; variant: 'h5' | 'body1' }) => {
    const { t } = useTranslation();

    if (value != null) {
        return <Typography variant={variant}>{value}</Typography>;
    }

    return (
        <Tooltip title={t('str.value-not-available.explanation')}>
            <Typography variant={variant} color="text.disabled">
                {t('str.value-not-available')}
            </Typography>
        </Tooltip>
    );
};

export type PostCardProps = {
    post: PostCard;
    /** Position in a ranked list, shown as a badge on the image. */
    rank?: number;
};

export const PostCard = ({ post, rank }: PostCardProps) => {
    const { t } = useTranslation();

    const postFormatIcon = (formatTransKey: string) => {
        const map: Record<string, React.ReactNode> = {
            [t('str.net-post.format.album')]: <PhotoLibraryRounded fontSize="small" />,
            [t('str.net-post.format.image')]: <ImageRounded fontSize="small" />,
            [t('str.net-post.format.video')]: <VideoCameraBackRounded fontSize="small" />,
            [t('str.net-post.format.link')]: <LinkRounded fontSize="small" />,
        };

        return map[formatTransKey];
    };

    // TODO: In the future could make more sense create a new metric for icon
    const postOwnerNetIcon = (netName: string) => {
        return `/social/${netName.replace(' ', '_').toLowerCase()}.svg`;
    };

    const [headline, ...otherMetrics] = post.metrics;

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                {post.profile && (
                    <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
                        {post.profile.avatar != null && (
                            <Avatar
                                variant="rounded"
                                src={post.profile.avatar}
                                sx={{ width: 32, height: 32 }}
                            >
                                {stringAvatar(post.profile.name)}
                            </Avatar>
                        )}
                        {post.profile.net != null && (
                            <Icon
                                component="img"
                                sx={{ height: 16, width: 16, flexShrink: 0 }}
                                src={postOwnerNetIcon(post.profile.net)}
                            />
                        )}
                        <Typography variant="subtitle2" noWrap title={post.profile.name}>
                            {post.profile.name}
                        </Typography>
                    </Stack>
                )}

                <Box sx={{ position: 'relative', height: IMAGE_HEIGHT, borderRadius: 2, overflow: 'hidden' }}>
                    <Avatar src={post.image} variant="square" sx={{ height: '100%', width: '100%' }}>
                        <ImageNotSupportedRounded />
                    </Avatar>
                    {rank != null && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: '#1A1B25',
                                color: '#E1E3F0',
                            }}
                        >
                            <Typography variant="subtitle2" component="span">
                                #{rank}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Stack direction="row" alignItems="center" gap={0.75} minHeight={28} color="text.disabled">
                    {post.format && (
                        <Tooltip title={post.format}>
                            <Box sx={{ display: 'flex' }}>{postFormatIcon(post.format)}</Box>
                        </Tooltip>
                    )}
                    <Typography variant="caption" noWrap flex={1}>
                        {post.publishDate}
                    </Typography>
                    {post.url != null && (
                        <Tooltip title={t('insights.widget-feed.view-on-net', { net: post.profile?.net })}>
                            <IconButton href={post.url} target="_blank" size="small" sx={{ mr: -0.5 }}>
                                <OpenInNewRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '3',
                        WebkitBoxOrient: 'vertical',
                        height: '3lh',
                    }}
                >
                    {post.caption}
                </Typography>

                {headline && (
                    <>
                        {/* Pushed to the bottom, so the numbers line up across the cards of a row. */}
                        <Divider sx={{ mt: 'auto' }} />
                        <Stack minWidth={0}>
                            <Typography variant="caption" color="text.secondary" noWrap title={headline.name}>
                                {headline.name}
                            </Typography>
                            <MetricValue value={headline.value} variant="h5" />
                        </Stack>
                        {otherMetrics.length > 0 && (
                            <Box sx={metricsGridSx}>
                                {otherMetrics.map((metric, key) => (
                                    <Stack key={key} minWidth={0}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            title={metric.name}
                                        >
                                            {metric.name}
                                        </Typography>
                                        <MetricValue value={metric.value} variant="body1" />
                                    </Stack>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};
